import type { Quantity, UnitCode } from '~/graphql/graphql';
import type { Dimensions } from '~/utils/unitConversion';
import { parsePriceInput, type ParsedPrice } from '~/utils/moneyBounds';
import { roundUpToBundleQty } from '~/utils/order';
import type { BundleProductLengthLike } from '~/components/ui/bundleQuantity';
import { hasBundleDimensions } from '~/components/ui/bundleQuantity';

export type LengthInput = {
  quantity: string;
  pickupPrice: string;
  deliveryPrice: string;
};

export const EMPTY_LENGTH_INPUT: LengthInput = {
  quantity: '',
  pickupPrice: '',
  deliveryPrice: '',
};

export const PRICE_FIELDS = [
  {
    field: 'pickupPrice',
    labelKey: 'order_builder.request_quote.pickup_price_for',
  },
  {
    field: 'deliveryPrice',
    labelKey: 'order_builder.request_quote.delivery_price_for',
  },
] as const;

export type RequestQuoteDisabledReason =
  'canned_tally' | 'no_ship_to' | 'no_rows';

export function requestQuoteDisabledReason({
  isCannedTally,
  hasShipTo,
  selectedCount,
}: {
  isCannedTally: boolean;
  hasShipTo: boolean;
  selectedCount: number;
}): RequestQuoteDisabledReason | null {
  if (isCannedTally) return 'canned_tally';
  if (!hasShipTo) return 'no_ship_to';
  if (selectedCount === 0) return 'no_rows';
  return null;
}

const MS_PER_DAY = 86_400_000;

function daysFromToday(date: string, today: Date): number {
  const [year, month, day] = date.split('-').map(Number);
  return Math.round(
    (Date.UTC(year, month - 1, day) -
      Date.UTC(today.getFullYear(), today.getMonth(), today.getDate())) /
      MS_PER_DAY
  );
}

export function leadTimeDaysUntil(
  neededBy: string,
  today: Date = new Date()
): number | undefined {
  if (!neededBy) return undefined;
  return Math.max(0, daysFromToday(neededBy, today));
}

export function isPastDate(date: string, today: Date = new Date()): boolean {
  return date !== '' && daysFromToday(date, today) < 0;
}

export function isPositiveQuantity(quantity: number): boolean {
  return Number.isFinite(quantity) && quantity > 0;
}

export function parseTargetPrice(value: string): ParsedPrice {
  return parsePriceInput(value);
}

export function quantityIsInvalid(
  selected: boolean,
  quantity: number
): boolean {
  return selected && !isPositiveQuantity(quantity);
}

export function priceIsInvalid(selected: boolean, value: string): boolean {
  return selected && !parseTargetPrice(value).valid;
}

export function bundleSizeIsMissing(
  selected: boolean,
  platformProductsLength: BundleProductLengthLike,
  unit: UnitCode
): boolean {
  return selected && !hasBundleDimensions(platformProductsLength, unit);
}

export type ItemsStepRow = {
  quantity: number;
  pickupPrice: ParsedPrice;
  deliveryPrice: ParsedPrice;
  platformProductsLength: BundleProductLengthLike;
  unit: UnitCode;
};

export function itemsStepIsValid(
  neededBy: string,
  selectedRows: ItemsStepRow[]
): boolean {
  return (
    !isPastDate(neededBy) &&
    selectedRows.length > 0 &&
    selectedRows.every(
      (row) =>
        hasBundleDimensions(row.platformProductsLength, row.unit) &&
        isPositiveQuantity(row.quantity) &&
        row.pickupPrice.valid &&
        row.deliveryPrice.valid
    )
  );
}

export function defaultRequestQuantity(
  value: number,
  unit: UnitCode,
  { lengthInches, widthInches, thicknessInches }: Dimensions,
  piecesPerBundle: number
): Quantity {
  const bundled = roundUpToBundleQty(
    value,
    piecesPerBundle,
    unit,
    lengthInches,
    widthInches,
    thicknessInches
  );
  return { value: Math.round(bundled * 1000) / 1000, unit };
}
